import React, { useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { IconType } from 'react-icons';
import {
  DASHBOARD_STAT_CARD_ORDER_STORAGE_KEY,
  resetDashboardStatCardOrderStorage,
} from '../components/dashboard/hooks/useDashboardStatCardOrder';
import {
  DASHBOARD_SECTION_VISIBILITY_STORAGE_KEY,
  resetDashboardSectionVisibilityStorage,
} from '../components/dashboard/hooks/useDashboardSectionVisibility';
import { resetDashboardRetentionCountriesStorage } from '../components/dashboard/utils/dashboardRetentionCountriesStorage';
import { useAuth } from '../context/AuthContext';

const Settings: React.FC = () => {
  const [message, setMessage] = useState<string>('');
  const { user } = useAuth();

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  const handleResetDashboardLayout = () => {
    const ok = resetDashboardStatCardOrderStorage();
    if (ok) {
      setMessage('Dashboard kart siralama ayarlari sifirlandi. Degisikligi gormek icin dashboard sayfasini yenileyin.');
      return;
    }
    setMessage('Sifirlama sirasinda bir hata olustu. Lutfen tekrar deneyin.');
  };

  const handleResetSectionFilters = () => {
    const ok = resetDashboardSectionVisibilityStorage();
    if (ok) {
      setMessage('Dashboard gorunum filtreleri sifirlandi. Acik dashboard varsa aninda guncellenir.');
      return;
    }
    setMessage('Sifirlama sirasinda bir hata olustu. Lutfen tekrar deneyin.');
  };

  const handleResetDashboardCountries = () => {
    const ok = resetDashboardRetentionCountriesStorage();
    if (ok) {
      setMessage(
        'Kayitli dashboard ulke secimi silindi. Varsayilan (Tum Ulkeler) icin dashboard sayfasini yenileyin.'
      );
      return;
    }
    setMessage('Sifirlama sirasinda bir hata olustu. Lutfen tekrar deneyin.');
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Ayarlar
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Dashboard kisisellestirme ayarlarinizi buradan yonetebilirsiniz.
        </p>
      </div>

      <section className="glass-strong rounded-2xl border border-slate-200/70 dark:border-slate-700/70 p-5 space-y-3">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Profil Bilgileri
        </h2>
        {user ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Ad Soyad</p>
              <p className="font-medium text-slate-800 dark:text-slate-100">{user.name}</p>
            </div>
            <div className="rounded-lg bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">E-posta</p>
              <p className="font-medium text-slate-800 dark:text-slate-100">{user.email}</p>
            </div>
            <div className="rounded-lg bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Rol</p>
              <p className="font-medium text-slate-800 dark:text-slate-100">{user.role}</p>
            </div>
            <div className="rounded-lg bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Kullanici ID</p>
              <p className="font-medium text-slate-800 dark:text-slate-100 break-all">{user.id}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Aktif kullanici bilgisi bulunamadi.
          </p>
        )}
      </section>

      <section className="glass-strong rounded-2xl border border-slate-200/70 dark:border-slate-700/70 p-5 space-y-3">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Dashboard Kart Sirasi
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Ust kartlar icin kaydedilen surukle-birak sirasini varsayilan duzene dondurur.
        </p>
        <button
          type="button"
          onClick={handleResetDashboardLayout}
          className="inline-flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          {renderIcon(FiRefreshCw, 'w-4 h-4')}
          Sifirla
        </button>
      </section>

      <section className="glass-strong rounded-2xl border border-slate-200/70 dark:border-slate-700/70 p-5 space-y-3">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Dashboard Ulke Secimi
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Retention ve uygulama metrikleri panolarinda kayitli ulke tercihini sifirlar.
        </p>
        <button
          type="button"
          onClick={handleResetDashboardCountries}
          className="inline-flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          {renderIcon(FiRefreshCw, 'w-4 h-4')}
          Sifirla
        </button>
      </section>

      <section className="glass-strong rounded-2xl border border-slate-200/70 dark:border-slate-700/70 p-5 space-y-3">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Dashboard Gorunum Filtreleri
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Ustteki filtrede hangi bolumlerin gorunecegini sifirlar; tum bolumler tekrar acilir.
        </p>
        <button
          type="button"
          onClick={handleResetSectionFilters}
          className="inline-flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          {renderIcon(FiRefreshCw, 'w-4 h-4')}
          Sifirla
        </button>
      </section>

      {message && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 p-3 text-sm text-slate-700 dark:text-slate-200">
          {message}
        </div>
      )}
    </div>
  );
};

export default Settings;
